import React, { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { esPregunta, recorrido, secciones, textoDeValor } from '../../../lib/formularios';
import { fechaLarga } from '../responder/utilidades';

/*
 * Imprimir respuestas (una o todas). Se monta en <body>, fuera del editor, y durante la
 * impresión la clase `rs-imprimiendo` esconde todo lo demás (respuestas.css). Tipografía sobria
 * y sin el lienzo del tema: el papel no necesita el color de fondo y así se lee en cualquier impresora.
 */
export default function Impresion({ formulario, respuestas, onTerminar }) {
  // En una ref: si quien la usa pasa una función nueva en cada render, no debe volver a imprimir
  const alTerminar = useRef(onTerminar);
  alTerminar.current = onTerminar;
  useEffect(() => {
    if (!respuestas?.length) return undefined;
    document.body.classList.add('rs-imprimiendo');
    const terminar = () => {
      document.body.classList.remove('rs-imprimiendo');
      alTerminar.current?.();
    };
    window.addEventListener('afterprint', terminar, { once: true });
    // Un fotograma para que el portal ya esté pintado cuando el navegador tome la "foto"
    const raf = requestAnimationFrame(() => setTimeout(() => window.print(), 50));
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener('afterprint', terminar);
      document.body.classList.remove('rs-imprimiendo');
    };
  }, [respuestas]);

  if (!respuestas?.length) return null;
  const es = !!formulario?.ajustes?.es_cuestionario;

  return createPortal(
    <div className="rs-impresion">
      {respuestas.map((r) => {
        const visitadas = recorrido(formulario, r.respuestas || {});
        const items = secciones(formulario.items).filter((s) => visitadas.includes(s.id)).flatMap((s) => s.items).filter(esPregunta);
        return (
          <article key={r.id} className="rs-impresion-respuesta">
            <header>
              <h1>{formulario.titulo || 'Formulario'}</h1>
              <p>
                Respuesta #{r.numero} · {fechaLarga(r.enviada_en)}
                {r.correo ? ` · ${r.correo}` : ''}
                {es && r.puntuacion ? ` · Puntuación: ${r.puntuacion.total}/${r.puntuacion.maximo}` : ''}
              </p>
            </header>
            {items.map((item) => {
              const v = r.respuestas?.[item.id];
              const p = r.puntuacion?.por_pregunta?.[item.id];
              return (
                <section key={item.id}>
                  <h2>
                    {item.titulo || 'Pregunta sin título'}
                    {es && p ? <span> ({p.puntos}/{p.maximo})</span> : null}
                  </h2>
                  <p className={v === undefined || v === null ? 'rs-impresion-vacia' : ''}>
                    {v === undefined || v === null ? 'Sin respuesta' : textoDeValor(item, v)}
                  </p>
                  {es && p?.retro ? <p className="rs-impresion-retro">Comentario: {p.retro}</p> : null}
                </section>
              );
            })}
          </article>
        );
      })}
    </div>,
    document.body,
  );
}
